import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { toast } from "@performyard/py-components";

import type { MeetingGoalAction } from "../types";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import {
	GoalAddMessageModal,
	GoalArchiveModal,
	GoalCompleteModal,
	GoalReopenModal,
	GoalUpdateProgressModal,
} from "../modals";

type ActiveModal = {
	action: MeetingGoalAction | null;
	goal: Goal | null;
};

type UseGoalActionModalsProps = {
	onGoalUpdated?: () => void;
};

type UseGoalActionModalsResult = {
	handleActionSelect: (action: MeetingGoalAction, goal: Goal) => void;
	modals: React.ReactNode;
};

export const useGoalActionModals = ({ onGoalUpdated }: UseGoalActionModalsProps): UseGoalActionModalsResult => {
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [activeModal, setActiveModal] = useState<ActiveModal>({ action: null, goal: null });

	// Invalidate all goal search queries to ensure all components showing goals are updated
	const invalidateGoalQueries = useCallback(async (): Promise<void> => {
		await queryClient.invalidateQueries({
			queryKey: ["search-service", SERVICE_GOAL],
		});
		onGoalUpdated?.();
	}, [queryClient, onGoalUpdated]);

	// Success handlers for each action with toast notifications
	const handleUpdateProgressSuccess = useCallback(async (): Promise<void> => {
		toast.success("Goal progress updated");
		await invalidateGoalQueries();
	}, [invalidateGoalQueries]);

	const handleCompleteSuccess = useCallback(async (): Promise<void> => {
		toast.success("Goal marked as completed");
		await invalidateGoalQueries();
	}, [invalidateGoalQueries]);

	const handleAddMessageSuccess = useCallback(async (): Promise<void> => {
		toast.success("Message added");
		await invalidateGoalQueries();
	}, [invalidateGoalQueries]);

	const handleArchiveSuccess = useCallback(async (): Promise<void> => {
		toast.success("Goal archived");
		await invalidateGoalQueries();
	}, [invalidateGoalQueries]);

	const handleReopenSuccess = useCallback(async (): Promise<void> => {
		toast.success("Goal reopened");
		await invalidateGoalQueries();
	}, [invalidateGoalQueries]);

	const handleActionSelect = (action: MeetingGoalAction, goal: Goal): void => {
		if (action === "addToAgenda") {
			return;
		}

		if (action === "edit") {
			navigate(`/goal/${goal._id}/edit/?returnTo=${encodeURIComponent(location.pathname)}`);
			return;
		}

		setActiveModal({ action, goal });
	};

	const handleCloseModal = (): void => {
		setActiveModal({ action: null, goal: null });
	};

	const modals = activeModal.goal ? (
		<>
			<GoalUpdateProgressModal
				goalId={activeModal.goal._id}
				isOpen={activeModal.action === "update"}
				onClose={handleCloseModal}
				onSuccess={handleUpdateProgressSuccess}
			/>
			<GoalCompleteModal
				goalId={activeModal.goal._id}
				isOpen={activeModal.action === "complete"}
				onClose={handleCloseModal}
				onSuccess={handleCompleteSuccess}
			/>
			<GoalAddMessageModal
				goalId={activeModal.goal._id}
				goalName={activeModal.goal.name}
				isOpen={activeModal.action === "addMessage"}
				onClose={handleCloseModal}
				onSuccess={handleAddMessageSuccess}
			/>
			<GoalArchiveModal
				goalId={activeModal.goal._id}
				goalName={activeModal.goal.name}
				isOpen={activeModal.action === "archive"}
				onClose={handleCloseModal}
				onSuccess={handleArchiveSuccess}
			/>
			<GoalReopenModal
				goalId={activeModal.goal._id}
				goalName={activeModal.goal.name}
				isOpen={activeModal.action === "reopen"}
				onClose={handleCloseModal}
				onSuccess={handleReopenSuccess}
			/>
		</>
	) : null;

	return { handleActionSelect, modals };
};
